// Two destinations, kept running in parallel rather than cut over. Every
// call below fans out across the whole list so neither stream silently
// drops data.
const GA_MEASUREMENT_IDS = ["G-WX5XEPJ67E", "G-FWGC4QS7SZ"];

// gtag.js only has one <script src>, but that loader is the same shared
// runtime regardless of which id it's fetched with -- the per-destination
// routing happens entirely via the 'config' calls in injectScript(). Pin
// this to an id we've verified is live: G-WX5XEPJ67E returned a 404 from
// googletagmanager.com right after it was added (09/01), which took the
// whole runtime down -- neither destination got any data, since the script
// tag itself never loaded. Fetching the loader through a working id means a
// bad second id can only ever break its own destination, not both.
const SCRIPT_LOADER_ID = "G-FWGC4QS7SZ";

let scriptInjected = false;

// Path last requested via trackPageView() before gtag was ready (consent not
// yet granted, or -- since CookieConsentContext and App mount in the same
// tick -- the consent effect just hasn't run yet). Flushed once the script
// finishes loading, so the page the user is actually on always gets counted
// instead of being silently dropped.
let pendingPath = null;

// Routes whose live value can carry PII/secrets and must never reach Google
// as-is: an emailed reset/verification token in the query string, or an
// email address in a path segment. Query strings are dropped entirely (see
// trackPageView), so only path-embedded cases need listing here.
const SENSITIVE_PATH_PATTERNS = [
  { pattern: /^\/checkemail\/.+$/, replacement: "/checkemail/:email" },
];

function sanitizePagePath(pathname) {
  const hit = SENSITIVE_PATH_PATTERNS.find(({ pattern }) => pattern.test(pathname));
  return hit ? hit.replacement : pathname;
}

// Origin + sanitized path, no query string. gtag derives `page_location` from
// `document.location.href` for every hit unless we override it, so passing a
// clean `page_path` alone is not enough: the raw URL (reset/verify tokens and
// all) would still reach Google. `gtag('set', ...)` makes the override stick
// for the auto events (scroll, outbound click, and so on) that follow too.
function sanitizedPageLocation(path) {
  return window.location.origin + sanitizePagePath(path);
}

function sendPageView(path) {
  window.gtag("set", { page_location: sanitizedPageLocation(path) });
  window.gtag("event", "page_view", { page_path: sanitizePagePath(path) });
}

function injectScript() {
  if (scriptInjected || document.getElementById("ga-gtag-script")) {
    scriptInjected = true;
    return;
  }

  const script = document.createElement("script");
  script.id = "ga-gtag-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${SCRIPT_LOADER_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // We send every page_view ourselves (see trackPageView), including the
  // first one via the pendingPath flush below -- gtag's built-in auto-pageview
  // only fires once here and would otherwise race with, or duplicate, that.
  // page_location is pinned to the sanitized current URL from the outset so
  // that even the events gtag fires on its own never carry the raw query
  // string (which can hold emailed reset/verify tokens). A 'config' call per
  // id registers each as its own destination; subsequent gtag('event', ...)
  // calls fan out to all of them without needing send_to.
  for (const id of GA_MEASUREMENT_IDS) {
    window.gtag("config", id, {
      send_page_view: false,
      page_location: sanitizedPageLocation(window.location.pathname),
    });
  }

  scriptInjected = true;

  if (pendingPath !== null) {
    const path = pendingPath;
    pendingPath = null;
    sendPageView(path);
  }
}

// Injects gtag.js and starts sending data. Safe to call repeatedly; only the
// first call does anything. Call this only once analytics consent is granted.
export function loadGoogleAnalytics() {
  injectScript();
  for (const id of GA_MEASUREMENT_IDS) {
    window[`ga-disable-${id}`] = false;
  }
}

// Stops gtag from sending further data, per Google's documented opt-out flag:
// https://developers.google.com/analytics/devguides/collection/gtagjs/user-opt-out
// Setting this is safe even if the script was never loaded.
export function disableGoogleAnalytics() {
  for (const id of GA_MEASUREMENT_IDS) {
    window[`ga-disable-${id}`] = true;
  }
}

// Call on every route change (including the very first). Pass location.pathname
// only -- never location.search -- since query strings on this site can carry
// bearer tokens (password reset, waitlist verify/status) that must not be
// exfiltrated to a third party. Path segments are sanitized too, since some
// (e.g. /checkemail/:email) embed an address rather than an opaque id.
export function trackPageView(path) {
  if (typeof window.gtag !== "function") {
    pendingPath = path;
    return;
  }
  sendPageView(path);
}
